import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, PlusCircle, CalendarCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { fmtINR, prettyDate } from "../lib/dates";
import { useAuth } from "../context/AuthContext";
import InquiryForm from "../components/InquiryForm";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";

const STATUS_STYLE = {
  new: "bg-accent/15 text-accent",
  contacted: "bg-primary/15 text-primary",
  closed: "bg-muted text-muted-foreground",
  pending: "bg-accent/15 text-accent",
  confirmed: "bg-primary/15 text-primary",
  cancelled: "bg-destructive/15 text-destructive",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/leads/mine").then(({ data }) => setLeads(data)).catch(() => toast.error("Failed to load inquiries"));
    api.get("/bookings/mine").then(({ data }) => setBookings(data)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, [open]);

  const cancelBooking = async (id) => {
    if (!window.confirm("Cancel this booking? Refund is per our cancellation policy (100% if 10+ days before check-in, else 10% fee).")) return;
    try {
      const { data } = await api.post(`/bookings/${id}/cancel-mine`);
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status: "cancelled", refund_amount: data.refund_amount } : b)));
      toast.success(`Cancelled · ${data.policy}. Refund: ${fmtINR(data.refund_amount)}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Cancel failed");
    }
  };

  return (
    <div className="min-h-[80vh] bg-secondary/40" data-testid="dashboard-page">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent">Client Portal</p>
            <h1 className="font-heading text-4xl font-light mt-2">Hello, {user?.name?.split(" ")[0] || "there"}</h1>
            <p className="text-muted-foreground mt-1">Track the inquiries you&apos;ve submitted to LAVista.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-inquiry-btn" className="rounded-full gap-2">
                <PlusCircle className="h-4 w-4" /> New Inquiry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-light">Submit a new inquiry</DialogTitle>
              </DialogHeader>
              <InquiryForm />
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-10 space-y-4">
          {bookings.length > 0 && (
            <div data-testid="my-bookings-section">
              <h2 className="font-heading text-2xl font-light mb-4 flex items-center gap-2"><CalendarCheck className="h-5 w-5 text-accent" /> My Villa Bookings</h2>
              <div className="space-y-4 mb-10">
                {bookings.map((b) => (
                  <div key={b.id} data-testid={`my-booking-${b.id}`} className="bg-card border border-border rounded-lg p-6 flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-heading text-xl">{b.villa_title}</h3>
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_STYLE[b.status] || STATUS_STYLE.pending}`}>{b.status}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{prettyDate(b.check_in)} → {prettyDate(b.check_out)} · {b.num_nights} nights · {b.guests} guests</p>
                      <p className="text-sm mt-1">Total: <span className="font-semibold text-accent">{fmtINR(b.total)}</span>
                        {b.payment_status && b.payment_status !== "unpaid" && <span className="text-muted-foreground"> · {b.payment_status.replace("_", " ")}</span>}
                        {b.status === "cancelled" && b.refund_amount != null && <span className="text-muted-foreground"> · refund {fmtINR(b.refund_amount)}</span>}
                      </p>
                    </div>
                    {b.status !== "cancelled" && (
                      <Button size="sm" variant="outline" data-testid={`cancel-booking-${b.id}`} onClick={() => cancelBooking(b.id)} className="rounded-full text-destructive border-destructive/40">
                        Cancel
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <h2 className="font-heading text-2xl font-light mb-4">My Inquiries</h2>
            </div>
          )}
          {leads.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-14 text-center" data-testid="dashboard-empty">
              <Inbox className="h-10 w-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
              <p className="font-heading text-2xl font-light">No inquiries yet</p>
              <p className="text-muted-foreground text-sm mt-1">Submit your first inquiry to get started.</p>
            </div>
          ) : (
            leads.map((l) => (
              <div key={l.id} data-testid={`my-lead-${l.id}`} className="bg-card border border-border rounded-lg p-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading text-xl">{l.service}</h3>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_STYLE[l.status] || STATUS_STYLE.new}`}>
                      {l.status}
                    </span>
                  </div>
                  {l.message && <p className="text-sm text-muted-foreground mt-2 max-w-xl">{l.message}</p>}
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(l.created_at).toLocaleDateString()}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
